import { OccupancyBucket } from '@hakimo-ui/hakimo/types';
import { useMemo } from 'react';
import {
  BucketChartFrame,
  bucketSlot,
  bucketTime,
  CHART_WIDTH,
  GUTTER_LEFT,
  GUTTER_RIGHT,
  PLOT_HEIGHT,
  plotY,
} from '../bucket-chart/BucketChartFrame';

interface Props {
  occupancy: OccupancyBucket[];
  /** A limit to draw across the occupancy row, e.g. a max_occupancy count */
  threshold?: number | null;
}

/**
 * Occupancy over time, as a step function.
 *
 * Buckets with nothing observed are drawn as gaps, never as zero: the camera
 * only uploads when something moves.
 */
export function OccupancyChart({ occupancy, threshold }: Props) {
  const slot = bucketSlot(occupancy.length);

  const yMax = useMemo(() => {
    const top = Math.max(1, ...occupancy.map((b) => b.max), threshold ?? 0);
    return Math.ceil(top * 1.15);
  }, [occupancy, threshold]);

  const yFor = (value: number) => plotY(value, yMax);

  const stepPath = useMemo(() => {
    let d = '';
    let open = false;
    occupancy.forEach((b, i) => {
      const x0 = GUTTER_LEFT + i * slot;
      const x1 = x0 + slot;
      if (b.observedSeconds === 0) {
        open = false;
        return;
      }
      const y = plotY(b.max, yMax);
      d += open ? ` H${x0} V${y} H${x1}` : ` M${x0},${y} H${x1}`;
      open = true;
    });
    return d.trim();
  }, [occupancy, slot, yMax]);

  return (
    <BucketChartFrame
      ariaLabel="Occupancy over time"
      bucketStarts={occupancy.map((b) => b.bucketStartUtc)}
      yMax={yMax}
    >
      {/* unobserved buckets as a faint band */}
      {occupancy.map((b, i) =>
        b.observedSeconds === 0 ? (
          <rect
            key={b.bucketStartUtc}
            x={GUTTER_LEFT + i * slot}
            y={0}
            width={slot}
            height={PLOT_HEIGHT}
            className="fill-onlight-bg-3 dark:fill-ondark-bg-3"
            opacity={0.6}
          >
            <title>{`${bucketTime(b.bucketStartUtc)} — no clips`}</title>
          </rect>
        ) : null
      )}

      {/* observed buckets: area under the step, for hover */}
      {occupancy.map((b, i) =>
        b.observedSeconds > 0 ? (
          <rect
            key={b.bucketStartUtc}
            x={GUTTER_LEFT + i * slot}
            y={yFor(b.max)}
            width={slot}
            height={PLOT_HEIGHT - yFor(b.max)}
            className="fill-teal-600"
            opacity={0.18}
          >
            <title>{`${bucketTime(b.bucketStartUtc)} — peak ${b.max}, mean ${
              b.mean
            }, ${b.clips} clip${b.clips === 1 ? '' : 's'}, ${
              b.observedSeconds
            }s observed`}</title>
          </rect>
        ) : null
      )}

      {/* threshold */}
      {threshold != null && threshold > 0 && (
        <>
          <line
            x1={GUTTER_LEFT}
            x2={CHART_WIDTH - GUTTER_RIGHT}
            y1={yFor(threshold)}
            y2={yFor(threshold)}
            className="stroke-red-500"
            strokeWidth={1.5}
            strokeDasharray="5 4"
          />
          <text
            x={CHART_WIDTH - GUTTER_RIGHT}
            y={yFor(threshold) - 4}
            textAnchor="end"
            className="fill-red-500"
            fontSize={10}
          >
            limit {threshold}
          </text>
        </>
      )}

      {/* the step line */}
      {stepPath && (
        <path
          d={stepPath}
          fill="none"
          className="stroke-teal-600 dark:stroke-teal-400"
          strokeWidth={2}
          strokeLinejoin="round"
        />
      )}
    </BucketChartFrame>
  );
}

export default OccupancyChart;
